import { useFeedback } from "../hooks/useFeedback";
import brandMark from '../assets/brand-mark.svg?raw';
import Icon from "../components/Icon";
import { Toast, PageTitle, Card, Btn, Modal, ConfirmModal, Spinner, Pagination } from "../components";
import { useState, useEffect, useRef } from "react";
import { apiFetch } from "../api";
import {
  buildAssinaturaAuditoria,
  formatAssinaturaAccessDateTime,
  formatAssinaturaDateTime,
  getAssinaturaBorderColor,
  getAssinaturaStatusDisplay,
} from "../utils/assinaturaStatus";
import { buildDocumentoResumo, getDocumentoCardDisplay } from "../utils/documentoResumo";
import { useTenant } from "../context/TenantContext";
import { getDestinoLabel, getDocumentoPedidoLabel } from "../utils/tenantLabels";

const PER_PAGE = 20;

const isPedidoDocument = (documento) => documento?.tipo_documento === "programacao";

const getPedidosOrigem = (documento) => {
  const pedidos = Array.isArray(documento?.pedidos_origem)
    ? documento.pedidos_origem
    : [];
  return [...new Set(
    pedidos.map((pedido) => String(pedido || "").trim()).filter(Boolean)
  )];
};

const esc = (s) => {
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#x27;' }[c]));
};

const printLocation = (latitude, longitude) => {
  if (latitude == null || longitude == null || String(latitude).trim() === '' || String(longitude).trim() === '') return '—';
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return '—';
  return `<a href="https://maps.google.com/?q=${lat},${lng}" target="_blank" rel="noopener noreferrer" style="color:#f97316;font-size:11px">Ver localização: ${lat.toFixed(4)}, ${lng.toFixed(4)}</a>`;
};

const toFiniteNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const sortFuncionariosByStopOrder = (assinaturas = []) => {
  return [...assinaturas].sort((a, b) => {
    const ordemA = toFiniteNumber(a?.ordem_parada);
    const ordemB = toFiniteNumber(b?.ordem_parada);

    if (ordemA != null && ordemB != null && ordemA !== ordemB) return ordemA - ordemB;
    if (ordemA != null) return -1;
    if (ordemB != null) return 1;

    return String(a?.funcionario_nome || '').localeCompare(String(b?.funcionario_nome || ''), 'pt-BR');
  });
};

const getStopOrderLabel = (assinatura, fallback) => {
  return toFiniteNumber(assinatura?.ordem_parada) ?? fallback;
};

const formatEstimatedTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getEstimatedDisplay = (assinatura) => {
  if (assinatura?.status === "recusado") return '—';
  const horarioEstimado = formatEstimatedTime(assinatura?.horario_estimado);
  if (horarioEstimado) return horarioEstimado;
  return '—';
};

const getEstimatedColumnLabel = (trecho, tipoDocumento) => {
  if (tipoDocumento === "programacao") {
    return trecho === "ida" ? "Embarque previsto (PG)" : "Desembarque previsto (PG)";
  }
  return trecho === "ida" ? "Embarque aprox." : "Desembarque aprox.";
};
const getEstimatedCardLabel = (trecho, tipoDocumento) => {
  if (tipoDocumento === "programacao") return trecho === "ida" ? "Embarque previsto pela PG" : "Desembarque previsto pela PG";
  return trecho === "ida" ? "Embarque aproximado" : "Desembarque aproximado";
};
const getResponseDate = (assinatura) => assinatura?.status === "recusado" ? assinatura?.recusado_em : assinatura?.assinado_em;
const getStatusText = (assinatura) => {
  const { icon, text } = getAssinaturaStatusDisplay(assinatura);
  return `${icon} ${text}`;
};
const getStatusColor = (assinatura) => getAssinaturaStatusDisplay(assinatura).color;
const getBorderColor = (assinatura) => getAssinaturaBorderColor(assinatura);

const renderAssinaturaAuditoria = (assinatura, context = {}) => {
  const audit = buildAssinaturaAuditoria(assinatura, context);
  if (!audit.length) return null;

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
      <div style={{ fontSize: '0.8125rem', color: "var(--muted)", marginBottom: 6, fontWeight: 600 }}>
        Auditoria
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {audit.map((event) => (
          <div key={event.key} style={{ fontSize: '0.8125rem', color: "var(--muted)", lineHeight: 1.45 }}>
            <span style={{ marginRight: 6 }}>{event.icon}</span>
            <span style={{ color: "var(--text)", fontWeight: 500 }}>{event.label}</span>
            {" — "}
            {event.accessTime ? formatAssinaturaAccessDateTime(event.em) : formatAssinaturaDateTime(event.em)}
            {event.detail ? (
              <span style={{ display: "block", marginLeft: 18, marginTop: 2, color: "#fca5a5" }}>
                Motivo: {event.detail}
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
};

const getEventStatusColor = (status) => {
  if (status === "finalizada") return "#22c55e";
  if (status === "falha" || String(status || "").includes("falha")) return "#ef4444";
  if (status === "aguardando_km_final") return "#fbbf24";
  if (status === "em_rota") return "#38bdf8";
  return "var(--muted)";
};

const ROUTE_EVENT_LABELS = {
  funcionario_recusou_viagem: "Passageiro recusou a viagem",
  rota_atualizada_recusa: "Rota recalculada após recusa",
  documento_km_final_criado: "Link de finalização preparado",
  documento_km_final_enviado: "Link de finalização enviado",
  documento_km_final_falhou: "Falha no link de finalização",
  lembrete_assinatura_enviado: "Lembrete manual enviado",
  lembrete_auto_funcionario_inicio_enviado: "Lembrete automático de passageiro",
  lembrete_auto_km_final_enviado: "Lembrete automático de finalização",
  rota_finalizada: "Rota finalizada",
};

const REMINDER_TYPE_LABELS = {
  funcionario: "Confirmação do passageiro",
  motorista_inicio: "Início da rota pelo motorista",
  motorista_km_final: "Finalização/KM final pelo motorista",
};

const renderDocumentoEventDetails = (event, assinaturas = []) => {
  const details = event?.detalhes;
  if (!details || typeof details !== "object" || Array.isArray(details)) return null;

  const assinaturasById = new Map(
    (assinaturas || [])
      .filter((assinatura) => assinatura?.id)
      .map((assinatura) => [Number(assinatura.id), assinatura])
  );
  const reminderRows = Array.isArray(details.lembretes)
    ? details.lembretes.map((lembrete) => {
      const assinatura = assinaturasById.get(Number(lembrete?.assinatura_id));
      return {
        key: `${lembrete?.tipo || "lembrete"}-${lembrete?.assinatura_id || "sem-id"}`,
        tipo: REMINDER_TYPE_LABELS[lembrete?.tipo] || String(lembrete?.tipo || "Lembrete").replaceAll("_", " "),
        nome: String(
          lembrete?.destinatario_nome
          || assinatura?.funcionario_nome
          || ""
        ).trim(),
      };
    })
    : [];
  const refusalName = String(details.funcionario_nome || "").trim();
  const refusalReason = String(details.observacao_recusa || "").trim();
  const routeLink = String(details.link_mapa || "").trim();
  const errorMessage = String(
    details.erro_mensagem
    || details.notificacao_erro
    || details.erro
    || ""
  ).trim();
  const isFinalizationNotice = [
    "documento_km_final_enviado",
    "documento_km_final_falhou",
  ].includes(event?.tipo);
  const finalizationRecipient = isFinalizationNotice
    ? (assinaturas || []).find((assinatura) => assinatura?.tipo === "finalizacao")
    : null;

  if (
    !refusalName
    && !refusalReason
    && !routeLink
    && !errorMessage
    && !reminderRows.length
    && !isFinalizationNotice
    && !details.tentativa
    && !details.total_mensagens
    && !details.status_comunicacao
  ) {
    return null;
  }

  return (
    <div style={{ display: "grid", gap: 7, marginTop: 3, fontSize: '0.875rem' }}>
      {(refusalName || refusalReason) && (
        <div style={{ color: "#fca5a5", lineHeight: 1.5 }}>
          {refusalName && <div><strong>Passageiro:</strong> {refusalName}</div>}
          {refusalReason && <div><strong>Motivo:</strong> {refusalReason}</div>}
        </div>
      )}
      {isFinalizationNotice && finalizationRecipient?.funcionario_nome && (
        <div style={{ color: "var(--muted)" }}>
          <strong style={{ color: "var(--text)" }}>Motorista:</strong> {finalizationRecipient.funcionario_nome}
        </div>
      )}
      {details.tentativa && (
        <div style={{ color: "var(--muted)" }}>
          Tentativa automática #{details.tentativa}
        </div>
      )}
      {details.status_comunicacao && (
        <div style={{ color: "var(--muted)" }}>
          Comunicação: {String(details.status_comunicacao).replaceAll("_", " ")}
          {details.canal ? ` · canal ${details.canal}` : ""}
        </div>
      )}
      {reminderRows.length > 0 && (
        <div style={{ display: "grid", gap: 4 }}>
          <strong style={{ color: "var(--text)" }}>
            Destinatário{reminderRows.length !== 1 ? "s" : ""}:
          </strong>
          {reminderRows.map((row) => (
            <div key={row.key} style={{ color: "var(--muted)" }}>
              • {row.tipo}{row.nome ? ` — ${row.nome}` : ""}
            </div>
          ))}
        </div>
      )}
      {routeLink && (
        <a
          href={routeLink}
          target="_blank"
          rel="noreferrer"
          style={{ color: "#fdba74", fontWeight: 700, width: "fit-content" }}
        >
          <Icon name="route" /> Abrir rota no Google Maps
        </a>
      )}
      {errorMessage && (
        <div style={{ color: "#fca5a5" }}>
          <strong>Falha:</strong> {errorMessage}
        </div>
      )}
    </div>
  );
};

const formatAuditoriaText = (assinatura) => {
  const audit = buildAssinaturaAuditoria(assinatura);
  if (!audit.length) return "";
  return audit
    .map((event) => `${event.icon} ${event.label}: ${formatAssinaturaDateTime(event.em)}`)
    .join(" · ");
};

// Número da van definido na montagem; documentos antigos mantêm a posição na rota.
const documentVanLabel = (doc) => {
  const numero = Number(doc.van_numero) || (Number.isInteger(Number(doc.grupo_index_operacional)) && doc.grupo_index_operacional != null ? Number(doc.grupo_index_operacional) + 1 : null);
  return numero ? `Van ${numero}` : `Rota #${doc.rota_id || doc.id}`;
};

export default function DocumentosPage({ userRole, initialDocumentId, onInitialDocumentOpened }) {
  const { config } = useTenant();
  // Usa a empresa de destino da viagem; o rótulo da transportadora fica como reserva.
  const formatTrechoLabel = (trecho, destino) => (
    trecho === "ida" ? `Ida → ${destino || getDestinoLabel(config)}` : "Volta → Casa"
  );
  const [docs, setDocs] = useState([]);
  const [loadError,setLoadError] = useState("");
  const [totalDocs, setTotalDocs] = useState(0);
  const [view, setView] = useState("rota");
  const [tabTotals, setTabTotals] = useState({ pedidos: 0, rotas: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detalhes, setDetalhes] = useState(null);
  const [loadingDetalhes, setLoadingDetalhes] = useState(false);
  const [page, setPage] = useState(1);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const { toast, toastType, showToast, showError, dismissToast } = useFeedback("documentos");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const skipSearchDebounceRef = useRef(true);
  const openedInitialDocumentRef = useRef(null);
  const detailRequestRef = useRef(0);
  useEffect(() => () => { detailRequestRef.current += 1; openedInitialDocumentRef.current = null; }, []);
  const closeDocument = () => {
    detailRequestRef.current += 1;
    setSelected(null); setDetalhes(null); setLoadingDetalhes(false);
    onInitialDocumentOpened?.();
  };
  const [valorInput, setValorInput] = useState("");
  const [savingValor, setSavingValor] = useState(false);

  const canDeleteDocuments = userRole === "superadmin";
  const canEditValor = userRole === "admin" || userRole === "superadmin";

  const formatCurrency = (value) => {
    if (value == null || value === "") return "";
    return Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  const maskCurrency = (raw) => {
    const digits = raw.replace(/\D/g, "");
    if (!digits) return "";
    const num = (parseInt(digits, 10) / 100).toFixed(2);
    return Number(num).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const parseCurrencyToNumber = (masked) => {
    if (!masked) return null;
    const clean = masked.replace(/\./g, "").replace(",", ".");
    const num = parseFloat(clean);
    return Number.isFinite(num) && num >= 0 ? num : null;
  };

  const handleValorChange = (e) => {
    setValorInput(maskCurrency(e.target.value));
  };

  const salvarValor = async () => {
    if (!detalhes) return;
    setSavingValor(true);
    try {
      const numValue = parseCurrencyToNumber(valorInput);
      await apiFetch(`/documentos/${detalhes.id}/valor`, {
        method: "PATCH",
        body: JSON.stringify({ valor_monetario: numValue }),
      });
      setDetalhes({ ...detalhes, valor_monetario: numValue });
      setDocs(prev => prev.map(d => d.id === detalhes.id ? { ...d, valor_monetario: numValue } : d));
      showToast("Valor salvo.");
    } catch (e) {
      showError(e.message);
    } finally {
      setSavingValor(false);
    }
  };

  const loadDocs = async (targetPage = page, query = searchQuery) => {
    setLoadError("");
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(targetPage),
        limit: String(PER_PAGE),
        visao: view,
      });
      if (query.trim()) params.set("q", query.trim());
      const data = await apiFetch(`/documentos?${params.toString()}`);
      if (Array.isArray(data)) {
        setDocs(data);
        setTotalDocs(data.length);
      } else {
        setDocs(Array.isArray(data?.items) ? data.items : []);
        setTotalDocs(Number(data?.total) || 0);
        setTabTotals({
          pedidos: Number(data?.totals?.pedidos) || 0,
          rotas: Number(data?.totals?.rotas) || 0,
        });
      }
    } catch (e) {
      setLoadError(e.message || "Não foi possível carregar os documentos.");
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (skipSearchDebounceRef.current) {
      skipSearchDebounceRef.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      setSearchQuery(searchTerm);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadDocs(page, searchQuery);
  }, [page, searchQuery, view]);

  useEffect(() => {
    const documentId = Number(initialDocumentId) || null;
    if (!documentId) {
      openedInitialDocumentRef.current = null;
      return;
    }
    if (openedInitialDocumentRef.current === documentId) return;
    openedInitialDocumentRef.current = documentId;
    const requestId = ++detailRequestRef.current;
    setSelected({ id: documentId });
    setLoadingDetalhes(true);
    apiFetch(`/documentos/${documentId}`)
      .then((details) => {
        if (requestId !== detailRequestRef.current) return;
        setSelected(details);
        setDetalhes(details);
        setView(isPedidoDocument(details) ? "pedido" : "rota");
        setPage(1);
        setValorInput(details.valor_monetario != null ? maskCurrency(Math.round(details.valor_monetario * 100).toString()) : "");
      })
      .catch((error) => {
        if (requestId !== detailRequestRef.current) return;
        setSelected(null);
        showError(error.message);
      })
      .finally(() => {
        if (requestId !== detailRequestRef.current) return;
        setLoadingDetalhes(false);
        onInitialDocumentOpened?.();
      });
  }, [initialDocumentId, showError]);

  const baixarPDF = (e, doc, det) => {
    e.stopPropagation();
    const d = det || detalhes;
    if (!d) return;
    const dataFmt = d.data ? d.data.substring(0, 10).split('-').reverse().join('/') : '';
    const trecho = formatTrechoLabel(d.trecho, d.destino_nome);
    const estimativaHeader = getEstimatedColumnLabel(d.trecho, d.tipo_documento);
    const motAss = (d.assinaturas || []).find(a => a.tipo === 'motorista');
    const finAss = (d.assinaturas || []).find(a => a.tipo === 'finalizacao');
    const funcAssinaturas = sortFuncionariosByStopOrder((d.assinaturas || []).filter(a => a.tipo !== 'motorista' && a.tipo !== 'finalizacao'));
    const naoDespachadosPdf = funcAssinaturas.filter(a => a.nao_despachado).length;
    const resumoPdf = buildDocumentoResumo({ assinaturas: d.assinaturas || [] });
    const kmRodados = (motAss?.km_veiculo && finAss?.km_veiculo)
      ? Number(finAss.km_veiculo) - Number(motAss.km_veiculo)
      : null;

    const motRow = motAss ? (() => {
      const status = getStatusText(motAss);
      const motAuditoria = formatAuditoriaText(motAss);
      const motAuditoriaHtml = motAuditoria
        ? `<div style="margin-top:4px;color:#6b7280;font-size:11px;line-height:1.45">${esc(motAuditoria)}</div>`
        : "";
      const loc = printLocation(motAss.latitude, motAss.longitude);
      const assinImg = motAss.status === "assinado" && motAss.assinatura_data
        ? motAss.assinatura_data.startsWith("texto:")
          ? `<td style="font-family:'Playwrite IE',cursive;font-size:16px;padding:8px 12px">${esc(motAss.assinatura_data.replace("texto:", ""))}</td>`
          : motAss.assinatura_data.startsWith("data:image/")
            ? `<td style="padding:4px 12px"><img src="${esc(motAss.assinatura_data)}" style="height:40px"/></td>`
            : `<td style="color:#999;padding:8px 12px">—</td>`
        : `<td style="color:#999;padding:8px 12px">—</td>`;
      return `<tr style="border-bottom:2px solid #d1d5db;background:#f9f9f9">
        <td style="padding:8px 12px">M</td>
        <td style="padding:8px 12px;font-weight:600">${esc(motAss.funcionario_nome)} <span style="font-size:11px;color:#666;font-weight:400">(Motorista)</span></td>
        <td style="padding:8px 12px;font-size:12px;color:#666">—</td>
        <td style="padding:8px 12px;font-size:12px;color:${getStatusColor(motAss)}">${esc(status)}</td>
        <td style="padding:8px 12px;font-size:12px;color:#666">${esc(motAss.cpf_informado) || '—'}</td>
        <td style="padding:8px 12px;font-size:12px">${loc}</td>
        <td style="padding:8px 12px;font-size:12px;color:#666"></td>
        <td style="padding:8px 12px"></td>
        ${assinImg}
      </tr><tr><td colspan="9" style="padding:0;height:8px;background:#fff"></td></tr>`;
    })() : '';

    const linhas = funcAssinaturas.map((ass, i) => {
      const status = getStatusText(ass);
      const motivoRecusa = ass.status === "recusado" && ass.observacao_recusa
        ? `<div style="margin-top:4px;color:#b91c1c"><strong>Motivo:</strong> ${esc(ass.observacao_recusa)}</div>`
        : "";
      const auditoria = formatAuditoriaText(ass);
      const auditoriaHtml = auditoria
        ? `<div style="margin-top:4px;color:#6b7280;font-size:11px;line-height:1.45">${esc(auditoria)}</div>`
        : "";
      const loc = printLocation(ass.latitude, ass.longitude);
      const assinImg = (ass.status === "assinado" || ass.status === "recusado") && ass.assinatura_data
        ? ass.assinatura_data.startsWith("texto:")
          ? `<td style="font-family:'Playwrite IE',cursive;font-size:16px;padding:8px 12px;${ass.status === "recusado" ? "color:#ef4444;" : ""}">${esc(ass.assinatura_data.replace("texto:", ""))}</td>`
          : ass.assinatura_data.startsWith("data:image/")
            ? `<td style="padding:4px 12px"><img src="${esc(ass.assinatura_data)}" style="height:40px;${ass.status === "recusado" ? "opacity:0.6;" : ""}"/></td>`
            : `<td style="color:#999;padding:8px 12px">—</td>`
        : `<td style="color:#999;padding:8px 12px">—</td>`;
      const ordemParada = getStopOrderLabel(ass, i + 1);
      const presencaLabel = ass.presenca === 'presente'
        ? ass.status === 'assinado'
          ? '<span style="color:#16a34a;font-weight:700">P</span>'
          : '<span style="color:#ca8a04;font-weight:700" title="Presente na van — assinatura pendente">P*</span>'
        : ass.presenca === 'ausente'
          ? '<span style="color:#ef4444;font-weight:700">A</span>'
          : '—';
      return `<tr style="border-bottom:1px solid #eee">
        <td style="padding:8px 12px">${esc(ordemParada)}</td>
        <td style="padding:8px 12px;font-weight:600">${esc(ass.funcionario_nome)}</td>
        <td style="padding:8px 12px;font-size:12px">${esc(ass.id_pedido_origem) || '—'}</td>
        <td style="padding:8px 12px;font-size:12px;color:${getStatusColor(ass)}">${esc(status)}${motivoRecusa}</td>
        <td style="padding:8px 12px;font-size:12px;font-weight:600">${esc(ass.cpf_informado) || '—'}</td>
        <td style="padding:8px 12px;font-size:12px">${loc}</td>
        <td style="padding:8px 12px;font-size:12px;font-weight:600">${esc(getEstimatedDisplay(ass))}</td>
        <td style="padding:8px 12px;font-size:13px;text-align:center">${presencaLabel}</td>
        ${assinImg}
      </tr>`;
    }).join("");

    // ✅ Campos de finalização integrados no grid do cabeçalho do PDF
    const gridExtras = `
      ${motAss?.status === "assinado" ? `
        <div class="item"><label>Início da rota</label><span>${new Date(motAss.horario_inicio_rota || motAss.assinado_em).toLocaleString("pt-BR")}</span></div>
        ${motAss.km_veiculo ? `<div class="item"><label>KM Partida</label><span>${esc(motAss.km_veiculo)} km</span></div>` : ''}
      ` : ''}
      ${finAss?.status === "assinado" ? `
        <div class="item"><label>Fim da rota</label><span>${new Date(finAss.assinado_em).toLocaleString("pt-BR")}</span></div>
        ${finAss.km_veiculo ? `<div class="item"><label>KM Final</label><span>${esc(finAss.km_veiculo)} km</span></div>` : ''}
        ${kmRodados !== null ? `<div class="item"><label>KM Rodados</label><span style="color:${kmRodados < 0 ? '#ef4444' : '#16a34a'}">${kmRodados} km</span></div>` : ''}
      ` : ''}
    `;

    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"/>
    <title>Comprovante - ${esc(d.regiao)}</title>
    <link href="https://fonts.googleapis.com/css2?family=Playwrite+IE&display=swap" rel="stylesheet"/>
    <style>
      body{font-family:'Segoe UI',sans-serif;padding:32px;color:#111;max-width:900px;margin:0 auto}
      h1{font-size:22px;font-weight:700;margin-bottom:4px}
      .sub{color:#666;font-size:13px;margin-bottom:24px}
      .grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;background:#f9f9f9;border-radius:8px;padding:16px;margin-bottom:24px}
      .item label{font-size:11px;text-transform:uppercase;color:#666;letter-spacing:.5px}
      .item span{display:block;font-weight:600;font-size:14px;margin-top:2px}
      table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:24px}
      th{background:#f3f4f6;padding:8px 12px;text-align:left;font-size:11px;text-transform:uppercase;color:#666;letter-spacing:.5px}
      .footer{margin-top:32px;font-size:11px;color:#999;text-align:center}
      @media print{body{padding:16px}}
    </style></head><body>
    <div style="display:flex;align-items:center;gap:12px;margin-bottom:20px">
      ${brandMark.replace('<svg ', '<svg width="40" height="40" ')}
      <div><div style="font-weight:700;font-size:18px">RotaFlex</div><div style="font-size:12px;color:#666">${d.tipo_documento === "programacao" ? esc(getDocumentoPedidoLabel(config)) : "Comprovante de Transporte"}</div></div>
    </div>
    <h1>${esc(d.regiao)}</h1>
    <p class="sub">Documento gerado em ${new Date().toLocaleString("pt-BR")}</p>
    <div class="grid">
    <div class="item"><label>N° Programação</label><span>${esc(d.id_pedido)}</span></div>
      <div class="item"><label>Data</label><span>${esc(dataFmt)}</span></div>
      <div class="item"><label>Horário</label><span>${esc(d.horario)}</span></div>
      <div class="item"><label>Trecho</label><span>${esc(trecho)}</span></div>
      <div class="item"><label>${d.tipo_documento === "programacao" ? "Motorista responsável" : "Motorista"}</label><span>${esc(d.motorista_nome)}</span></div>
      <div class="item"><label>${d.tipo_documento === "programacao" ? "Veículo responsável" : "Veículo"}</label><span>${esc(d.veiculo)}</span></div>
      <div class="item"><label>Respostas no app</label><span>${resumoPdf.respondidos} / ${resumoPdf.total}</span></div>
      <div class="item"><label>Confirmaram</label><span>${resumoPdf.assinados}</span></div>
      <div class="item"><label>Recusas</label><span>${resumoPdf.recusados}</span></div>
      ${resumoPdf.pendentes > 0 ? `<div class="item"><label>Pendentes</label><span>${resumoPdf.pendentes}</span></div>` : ''}
      ${resumoPdf.finalizacaoOk ? `<div class="item"><label>Status da rota</label><span>Finalizada</span></div>` : ''}
      ${d.valor_monetario != null ? `<div class="item"><label>Valor</label><span style="color:#16a34a;font-size:16px">${Number(d.valor_monetario).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</span></div>` : ''}
      ${gridExtras}
    </div>
    <table>
      <thead><tr><th>#</th><th>Nome</th><th>Prog. original</th><th>Status</th><th>Chapa/CPF</th><th>Localização</th><th>${esc(estimativaHeader)}</th><th style="text-align:center">Pres.</th><th>Assinatura</th></tr></thead>
      <tbody>${motRow}${linhas}</tbody>
    </table>
    ${d.tipo_documento === "programacao" ? `<div style="font-size:11px;color:#6b7280;margin-top:10px">As previsões acima foram calculadas sobre a composição original da programação e são estimativas de planejamento.${naoDespachadosPdf > 0 ? ` ${naoDespachadosPdf} passageiro(s) do pedido ainda ${naoDespachadosPdf !== 1 ? 'aguardam' : 'aguarda'} envio do link de assinatura.` : ''}</div>` : ''}
    <div class="footer" style="margin-top:32px">RotaFlex — Sistema de Gestão de Rotas · ${new Date().toLocaleDateString("pt-BR")}</div>
    <script>window.onload=()=>{setTimeout(()=>{window.print();},400)}<\/script>
    </body></html>`;

    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
  };

  const abrirDocumento = async (doc) => {
    const requestId = ++detailRequestRef.current;
    setSelected(doc);
    setLoadingDetalhes(true);
    try {
      const d = await apiFetch(`/documentos/${doc.id}`);
      if (requestId !== detailRequestRef.current) return;
      setDetalhes(d);
      setSelected(d);
      setView(isPedidoDocument(d) ? "pedido" : "rota");
      setPage(1);
      setValorInput(d.valor_monetario != null ? maskCurrency(Math.round(d.valor_monetario * 100).toString()) : "");
    } catch (e) { if (requestId === detailRequestRef.current) showError(e.message); }
    finally { if (requestId === detailRequestRef.current) setLoadingDetalhes(false); }
  };

  const excluirDocumento = async (doc) => {
    if (!doc?.id) return;
    setDeletingId(doc.id);
    try {
      await apiFetch(`/documentos/${doc.id}`, { method: "DELETE" });
      if (selected?.id === doc.id) {
        setSelected(null);
        setDetalhes(null);
      }
      showToast("Documento excluído.");
      await loadDocs(page, searchQuery);
    } catch (e) {
      showError(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    const s = d.toString().substring(0, 10); // "2026-04-14"
    const [year, month, day] = s.split('-');
    return `${day}/${month}/${year}`;
  };
  const formatDateTime = (d) => new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const progressColor = (total, assinados) => {
    if (total === 0) return "var(--muted)";
    const pct = assinados / total;
    if (pct === 1) return "#22c55e";
    if (pct >= 0.5) return "#eab308";
    return "#f97316";
  };

  return (
    <div className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <PageTitle icon="document">Documentos</PageTitle>
          <p style={{ color: "var(--muted)", fontSize: '1rem', marginTop: 2 }}>
            {view === "pedido"
              ? `${getDocumentoPedidoLabel(config)} · demanda de transporte`
              : "Documentos das viagens e assinaturas"}
            {" "}— {totalDocs} documento{totalDocs !== 1 ? "s" : ""}
            {searchQuery.trim() ? ` · busca: "${searchQuery.trim()}"` : ""}
          </p>
        </div>
        <Btn variant="secondary" onClick={() => loadDocs(page, searchQuery)}><Icon name="refresh" /> Atualizar</Btn>
      </div>
      <div
        role="tablist"
        aria-label="Tipo de documento"
        style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}
      >
        {[
          {
            id: "rota",
            label: "Documentos das rotas",
            count: tabTotals.rotas,
            description: "Passageiros transportados, confirmações e finalização de cada van",
          },
          {
            id: "pedido",
            label: "Documentos dos pedidos",
            count: tabTotals.pedidos,
            description: "PG original, manifesto solicitado e previsões do pedido",
          },
        ].map((tab) => {
          const active = view === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              title={tab.description}
              onClick={() => {
                if (view === tab.id) return;
                setView(tab.id);
                setPage(1);
              }}
              style={{
                border: `1px solid ${active ? "rgba(249,115,22,.65)" : "var(--border)"}`,
                background: active ? "rgba(249,115,22,.14)" : "var(--s2)",
                color: active ? "#fdba74" : "var(--text)",
                borderRadius: 999,
                padding: "9px 14px",
                fontSize: '0.9375rem',
                fontWeight: 700,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {tab.label}
              <span style={{
                minWidth: 22,
                height: 22,
                padding: "0 6px",
                borderRadius: 999,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: active ? "rgba(249,115,22,.24)" : "var(--s3)",
                fontSize: '0.8125rem',
              }}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>
      <div style={{ marginBottom: 20 }}>
        <label className="rf-field-label" htmlFor="documentos-busca">Buscar documentos</label>
        <div style={{ position: "relative", marginTop: 6 }}>
          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: '1rem', color: "var(--muted)", pointerEvents: "none" }}><Icon name="search" /></span>
          <input
            type="search"
            id="documentos-busca"
            aria-label="Buscar documentos"
            placeholder={view === "pedido"
              ? "Buscar por N° do pedido, motorista ou data"
              : "Buscar por rota, pedido de origem, motorista, região ou data"}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 12px 10px 38px",
              background: "var(--s2)",
              border: "1px solid var(--border)",
              borderRadius: 10,
              color: "var(--text)",
              fontSize: '1rem',
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={e => e.target.style.borderColor = "#f97316"}
            onBlur={e => e.target.style.borderColor = "var(--border)"}
          />
        </div>
      </div>

      {!loading && loadError && <div role="alert" className="rf-inline-notice"><p>{loadError}</p><Btn variant="secondary" onClick={() => loadDocs()}>Tentar novamente</Btn></div>}
      {loading && <Card style={{ textAlign: "center", padding: 40, color: "var(--muted)" }}><Spinner /></Card>}

      {!loading && !loadError && totalDocs === 0 && !searchQuery.trim() && (
        <Card style={{ textAlign: "center", padding: 48 }}>
          <div style={{ fontSize: '2.25rem', marginBottom: 12, opacity: 0.3 }}><Icon name="document" /></div>
          <div style={{ fontWeight: 600, fontSize: '1rem', marginBottom: 8 }}>
            {view === "pedido" ? "Nenhum documento de pedido ainda" : "Nenhum documento de rota ainda"}
          </div>
          <div style={{ color: "var(--muted)", fontSize: '1rem', lineHeight: 1.6, maxWidth: 320, margin: "0 auto" }}>
            {view === "pedido"
              ? "Os documentos dos pedidos aparecem após o planejamento e o disparo das respectivas vans."
              : "Os comprovantes operacionais aparecem após o disparo da van ao motorista."}
          </div>
        </Card>
      )}

      {!loading && !loadError && totalDocs === 0 && searchQuery.trim() && (
        <Card style={{ textAlign: "center", padding: 48 }}>
          <div style={{ fontSize: '2.25rem', marginBottom: 12, opacity: 0.3 }}><Icon name="search" /></div>
          <div style={{ fontWeight: 600, fontSize: '1rem', marginBottom: 8 }}>Nenhum documento encontrado</div>
          <div style={{ color: "var(--muted)", fontSize: '1rem', lineHeight: 1.6, maxWidth: 360, margin: "0 auto" }}>
            Nenhum resultado para "<strong>{searchTerm}</strong>". Tente outro número de pedido, motorista ou data.
          </div>
          <Btn variant="secondary" style={{ marginTop: 16 }} onClick={() => { setSearchTerm(""); setSearchQuery(""); setPage(1); }}>Limpar busca</Btn>
        </Card>
      )}

      {!loading && docs.length > 0 && (
        <div style={{ display: "grid", gap: 12 }}>
          {docs.map(doc => {
            const pedidoDocument = isPedidoDocument(doc);
            const pedidosOrigem = getPedidosOrigem(doc);
            // Passageiros e motorista são contados separadamente, como no detalhe.
            const resumo = buildDocumentoResumo({
              totalFuncionarios: doc.total_passageiros ?? doc.total_funcionarios,
              totalAssinados: doc.passageiros_confirmados ?? doc.total_assinados,
              totalRecusados: doc.total_recusados,
              finalizacaoAssinada: doc.finalizacao_assinada,
            });
            const card = getDocumentoCardDisplay(resumo);
            return (
              <Card key={doc.id} style={{ cursor: "pointer" }} onClick={() => abrirDocumento(doc)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: "1 1 260px" }}>
                    <div style={{ width: 40, height: 40, flexShrink: 0, background: "rgba(249,115,22,0.12)", border: "1px solid rgba(249,115,22,0.2)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: '1.125rem' }}><Icon name="document" /></div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '1rem', overflowWrap: "anywhere" }}>
                        {pedidoDocument
                          ? `${doc.id_pedido ? `Pedido ${doc.id_pedido}` : "Pedido"}${doc.regiao ? ` · ${doc.regiao}` : ""}`
                          : `${documentVanLabel(doc)}${doc.regiao ? ` · ${doc.regiao}` : ""}`}
                        <span style={{
                          marginLeft: 8,
                          display: "inline-block",
                          whiteSpace: "nowrap",
                          fontSize: '0.875rem',
                          padding: "3px 7px",
                          borderRadius: 999,
                          background: pedidoDocument ? "rgba(34,197,94,.14)" : "rgba(59,130,246,.14)",
                          color: pedidoDocument ? "#86efac" : "#93c5fd",
                        }}>
                          {pedidoDocument ? "Documento do pedido" : "Documento da rota"}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.875rem', color: "var(--muted)", marginTop: 2 }}>
                        {formatDate(doc.data)} · {doc.trecho === "ida" ? "Ida" : "Volta"} · {doc.horario} · Motorista: {doc.motorista_nome}{!pedidoDocument && doc.rota_id ? ` · Rota #${doc.rota_id}` : ""}
                      </div>
                      {!pedidoDocument && pedidosOrigem.length > 0 && (
                        <div style={{ fontSize: '0.875rem', color: "#cbd5e1", marginTop: 5 }}>
                          <Icon name="document" /> Pedido{pedidosOrigem.length !== 1 ? "s" : ""} de origem: {pedidosOrigem.join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0, display: "grid", gap: 8, justifyItems: "end" }}>
                    <div>
                      <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: card.cor }}>
                        {resumo.finalizacaoOk && <Icon name="check" size={15} />} {card.titulo}
                      </div>
                      <div style={{ fontSize: '0.8125rem', color: "var(--muted)", marginTop: 2, lineHeight: 1.45 }}>
                        {card.subtitulo}
                      </div>
                      {card.detalhe && (
                        <div style={{ fontSize: '0.8125rem', color: "var(--muted)", marginTop: 2 }}>
                          {card.detalhe}
                        </div>
                      )}
                      {!pedidoDocument && doc.motorista_status !== undefined && (
                        <div style={{ fontSize: '0.8125rem', color: "var(--muted)", marginTop: 2 }}>
                          Motorista: {doc.motorista_status === 'assinado' ? 'iniciou a rota' : 'ainda não iniciou'}
                        </div>
                      )}
                    </div>
                    <Btn variant="secondary" size="sm" onClick={(event) => { event.stopPropagation(); abrirDocumento(doc); }} aria-label={`Abrir documento ${pedidoDocument ? 'do pedido' : 'da rota'} ${doc.rota_id || doc.id}`}>
                      <Icon name="eye" size={16} /> Abrir documento
                    </Btn>
                    {canDeleteDocuments && (
                      <Btn
                        size="sm"
                        variant="danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteDialog(doc);
                        }}
                        disabled={deletingId === doc.id}
                      >
                        Excluir
                      </Btn>
                    )}
                  </div>
                </div>
                <div style={{ marginTop: 12, height: 4, background: "var(--s3)", borderRadius: 2, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${card.progresso}%`, background: card.cor, borderRadius: 2, transition: "width 0.3s" }} />
                </div>
              </Card>
            );
          })}
          <Pagination page={page} total={totalDocs} perPage={PER_PAGE} onChange={setPage} />
        </div>
      )}

      {selected && (
        <Modal title={isPedidoDocument(detalhes || selected) ? "Documento do pedido" : "Documento da rota"}
          maxWidth={720} closeLabel="Fechar documento" onClose={closeDocument}
          actions={detalhes ? <>
            <Btn size="sm" variant="secondary" onClick={(event) => baixarPDF(event, selected, detalhes)}><Icon name="download" /> Baixar PDF</Btn>
            {canDeleteDocuments && <Btn size="sm" variant="danger" onClick={() => setDeleteDialog(selected)} disabled={deletingId === selected?.id}>Excluir</Btn>}
          </> : null}>
            {loadingDetalhes && <div style={{ textAlign: "center", padding: 40, color: "var(--muted)" }}><Spinner /></div>}

            {detalhes && (() => {
              const documentAuditContext = {
                documentoId: detalhes.id,
                documentoEventos: detalhes.eventos || [],
              };
              return (
              <>
                {(() => {
                  const resumoDet = buildDocumentoResumo({ assinaturas: detalhes.assinaturas || [] });
                  const cardDet = getDocumentoCardDisplay(resumoDet);
                  return (
                    <div style={{
                      background: `${cardDet.cor}14`,
                      border: `1px solid ${cardDet.cor}55`,
                      borderRadius: 10,
                      padding: "12px 16px",
                      marginBottom: 16,
                    }}>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: cardDet.cor }}>
                        {resumoDet.finalizacaoOk && <Icon name="check" size={15} />} {cardDet.titulo}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: "var(--muted)", marginTop: 4, lineHeight: 1.5 }}>
                        {cardDet.subtitulo}
                        {cardDet.detalhe ? ` · ${cardDet.detalhe}` : ""}
                      </div>
                    </div>
                  );
                })()}

                {/* ✅ Cabeçalho unificado — inclui dados de início/fim de rota e KM */}
                {(() => {
                  const motAss = detalhes.assinaturas?.find(a => a.tipo === 'motorista');
                  const finAss = detalhes.assinaturas?.find(a => a.tipo === 'finalizacao');
                  const recusas = detalhes.assinaturas?.filter(a => a.tipo !== "finalizacao" && a.status === "recusado").length || 0;
                  const kmRodados = (motAss?.km_veiculo && finAss?.km_veiculo)
                    ? Number(finAss.km_veiculo) - Number(motAss.km_veiculo)
                    : null;

                  const items = [
                    ["Data", formatDate(detalhes.data)],
                    ["Horário", detalhes.horario],
                    ["Trecho", formatTrechoLabel(detalhes.trecho, detalhes.destino_nome)],
                    ["Região", detalhes.regiao],
                    ["Motorista", detalhes.motorista_nome],
                    ["Veículo", detalhes.veiculo],
                    ...(recusas ? [["Recusas", recusas]] : []),
                    ...(detalhes.id_pedido ? [["Pedido", detalhes.id_pedido]] : []),
                    // Início da rota e KM partida (quando motorista assinou)
                    ...(motAss?.status === "assinado" ? [
                      ["Início da rota", formatDateTime(motAss.horario_inicio_rota || motAss.assinado_em)],
                      ...(motAss.km_veiculo ? [["KM Partida", `${motAss.km_veiculo} km`]] : []),
                    ] : []),
                    // Fim da rota e KM final (quando finalizou)
                    ...(finAss?.status === "assinado" ? [
                      ["Fim da rota", formatDateTime(finAss.assinado_em)],
                      ...(finAss.km_veiculo ? [["KM Final", `${finAss.km_veiculo} km`]] : []),
                    ] : []),
                    // KM rodados
                    ...(kmRodados !== null ? [["KM Rodados", `${kmRodados} km`]] : []),
                    // Valor monetário
                    ...(detalhes.valor_monetario != null ? [["Valor", formatCurrency(detalhes.valor_monetario)]] : []),
                  ];

                  return (
                    <div style={{ background: "var(--s2)", borderRadius: 10, padding: 16, marginBottom: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      {items.map(([k, v]) => {
                        const isKmRodados = k === "KM Rodados";
                        const kmNegativo = isKmRodados && kmRodados < 0;
                        const kmPositivo = isKmRodados && kmRodados >= 0;
                        return (
                          <div key={k}>
                            <div style={{ fontSize: '0.8125rem', color: "var(--muted)", marginBottom: 2 }}>{k}</div>
                            {isKmRodados ? (
                              <div style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: '1rem',
                                fontWeight: 700,
                                padding: "4px 10px",
                                borderRadius: 999,
                                background: kmPositivo ? "rgba(22,163,74,0.14)" : "rgba(239,68,68,0.14)",
                                color: kmPositivo ? "#16a34a" : "#ef4444",
                              }}>
                                <span>{kmPositivo ? "▲" : "▼"}</span>
                                <span>{v}</span>
                                <span style={{ fontSize: '0.8125rem', fontWeight: 600, opacity: 0.8 }}>
                                  {kmPositivo ? "positivo" : "negativo"}
                                </span>
                              </div>
                            ) : (
                              <div style={{ fontSize: '1rem', fontWeight: 600, color: "var(--text)" }}>{v}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {detalhes.tipo_documento === "programacao" && (() => {
                  const funcAss = (detalhes.assinaturas || []).filter((a) => a.tipo === "funcionario");
                  const assinados = funcAss.filter((a) => a.status === "assinado").length;
                  const base = Math.max(Number(detalhes.total_funcionarios || 0), funcAss.length);
                  const aguardandoEnvio = funcAss.filter((a) => a.nao_despachado).length;
                  return (
                    <div style={{ background: "var(--s2)", borderRadius: 10, padding: 16, marginBottom: 20, border: `1px solid ${aguardandoEnvio > 0 ? "rgba(234,179,8,0.35)" : "var(--border)"}` }}>
                      <div style={{ fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, marginBottom: 8 }}>
                        Passageiros do pedido
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: "var(--text)" }}>
                        {assinados} de {base} assinaram
                      </div>
                      <div style={{ fontSize: '0.875rem', color: "var(--muted)", marginTop: 4 }}>
                        {base} passageiro{base !== 1 ? "s" : ""} no pedido original
                      </div>
                      {aguardandoEnvio > 0 && (
                        <div style={{ fontSize: '0.875rem', color: "#eab308", marginTop: 6, fontWeight: 600 }}>
                          ⏳ {aguardandoEnvio} passageiro{aguardandoEnvio !== 1 ? "s" : ""} do pedido ainda {aguardandoEnvio !== 1 ? "aguardam" : "aguarda"} envio do link de assinatura.
                        </div>
                      )}
                    </div>
                  );
                })()}

                {detalhes.tipo_documento === "programacao" && Array.isArray(detalhes.rotas_relacionadas) && (
                  <div style={{ background: "var(--s2)", borderRadius: 10, padding: 16, marginBottom: 20, border: "1px solid var(--border)" }}>
                    <div style={{ fontSize: '0.875rem', color: "var(--muted)", fontWeight: 700, marginBottom: 10 }}>
                      Rotas que transportaram passageiros deste pedido
                    </div>
                    {detalhes.rotas_relacionadas.length > 0 ? (
                      <div style={{ display: "grid", gap: 8 }}>
                        {detalhes.rotas_relacionadas.map((rotaRelacionada) => (
                          <button
                            key={rotaRelacionada.documento_id}
                            type="button"
                            onClick={() => abrirDocumento({ id: rotaRelacionada.documento_id })}
                            style={{
                              width: "100%",
                              border: "1px solid var(--border)",
                              background: "var(--s3)",
                              color: "var(--text)",
                              borderRadius: 9,
                              padding: "10px 12px",
                              textAlign: "left",
                              cursor: "pointer",
                              display: "grid",
                              gap: 4,
                            }}
                          >
                            <span style={{ fontWeight: 700, fontSize: '0.9375rem' }}>
                              Rota #{rotaRelacionada.rota_id}
                              {rotaRelacionada.grupo_index_operacional != null
                                ? ` · Van ${Number(rotaRelacionada.grupo_index_operacional) + 1}`
                                : ""}
                              {rotaRelacionada.regiao ? ` · ${rotaRelacionada.regiao}` : ""}
                            </span>
                            <span style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
                              {rotaRelacionada.motorista_nome || "Motorista não informado"} · {rotaRelacionada.veiculo || "Veículo não informado"} · {rotaRelacionada.passageiros_deste_pedido || 0} passageiro(s) deste pedido
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: "var(--muted)", fontSize: '0.9375rem' }}>
                        Nenhuma van vinculada a este pedido.
                      </div>
                    )}
                  </div>
                )}

                {!isPedidoDocument(detalhes) && getPedidosOrigem(detalhes).length > 0 && (
                  <div style={{ background: "var(--s2)", borderRadius: 10, padding: 16, marginBottom: 20, border: "1px solid var(--border)" }}>
                    <div style={{ fontSize: '0.875rem', color: "var(--muted)", fontWeight: 700, marginBottom: 8 }}>
                      Pedidos de origem desta rota
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {getPedidosOrigem(detalhes).map((pedido) => (
                        <span
                          key={pedido}
                          style={{
                            borderRadius: 999,
                            background: "rgba(59,130,246,.14)",
                            color: "#93c5fd",
                            padding: "5px 9px",
                            fontSize: '0.875rem',
                            fontWeight: 700,
                          }}
                        >
                          <Icon name="document" /> {pedido}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Campo Valor Monetário */}
                {canEditValor && (
                <div style={{ background: "var(--s2)", borderRadius: 10, padding: 16, marginBottom: 20, border: "1px solid var(--border)" }}>
                  <label htmlFor="documento-valor" className="rf-field-label" style={{ marginBottom: 8 }}>Valor do comprovante</label>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <div style={{ position: "relative", flex: 1 }}>
                      <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--muted)", fontSize: '1rem', fontWeight: 600 }}>R$</span>
                      <input
                        id="documento-valor"
                        type="text" inputMode="decimal"
                        placeholder="0,00"
                        value={valorInput}
                        onChange={handleValorChange}
                        style={{
                          width: "100%",
                          padding: "10px 12px 10px 42px",
                          background: "var(--s3)",
                          border: "1px solid var(--border)",
                          borderRadius: 8,
                          color: "var(--text)",
                          fontSize: '1rem',
                          fontWeight: 600,
                          outline: "none",
                          transition: "border-color 0.2s",
                        }}
                        onFocus={e => e.target.style.borderColor = "#f97316"}
                        onBlur={e => e.target.style.borderColor = "var(--border)"}
                      />
                    </div>
                    <Btn onClick={salvarValor} disabled={savingValor}>{savingValor ? <><Spinner /> Salvando…</> : "Salvar valor"}</Btn>
                  </div>
                  {detalhes.valor_monetario != null && (
                    <div style={{ fontSize: '0.875rem', color: "#16a34a", marginTop: 8, fontWeight: 600 }}>
                      Valor atual: {formatCurrency(detalhes.valor_monetario)}
                    </div>
                  )}
                </div>
                )}

                {/* Seção Motorista */}
                {(() => {
                  const motAss = detalhes.assinaturas?.find(a => a.tipo === 'motorista');
                  if (!motAss) return null;
                  const motStatus = getAssinaturaStatusDisplay(motAss);
                  return (
                    <>
                      <div style={{ fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, marginBottom: 12 }}>
                        Motorista
                      </div>
                      <div style={{ background: "var(--s2)", borderRadius: 10, padding: 14, border: `1px solid ${getBorderColor(motAss)}`, marginBottom: 20 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '1rem' }}><Icon name="fleet" /> {motAss.funcionario_nome}</div>
                            <div style={{ fontSize: '0.875rem', color: motStatus.color, marginTop: 4 }}>
                              {motStatus.icon} {motStatus.text}
                              {motAss.status === "assinado" && motAss.km_veiculo && (
                                <span style={{ color: "var(--muted)", marginLeft: 8 }}><Icon name="fleet" /> KM partida: {motAss.km_veiculo}</span>
                              )}
                            </div>
                          </div>

                        </div>
                        {renderAssinaturaAuditoria(motAss)}
                      </div>
                    </>
                  );
                })()}

                {/* Seção Finalização — link/KM final do motorista (separado do início da rota) */}
                {(() => {
                  const motAss = detalhes.assinaturas?.find(a => a.tipo === 'motorista');
                  const finAss = detalhes.assinaturas?.find(a => a.tipo === 'finalizacao');
                  if (!finAss) return null;
                  const finContext = {
                    motoristaInicioEm: motAss?.horario_inicio_rota || motAss?.assinado_em || null,
                    kmFinalDispararEm: detalhes.km_final_disparar_em || null,
                    kmFinalDisparadoEm: detalhes.km_final_disparado_em || null,
                    ...documentAuditContext,
                  };
                  const finStatus = getAssinaturaStatusDisplay(finAss, finContext);
                  return (
                    <>
                      <div style={{ fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, marginBottom: 12 }}>
                        Finalização da rota
                      </div>
                      <div style={{ background: "var(--s2)", borderRadius: 10, padding: 14, border: `1px solid ${getBorderColor(finAss)}`, marginBottom: 20 }}>
                        <div style={{ fontSize: '0.875rem', color: finStatus.color, lineHeight: 1.5 }}>
                          {finStatus.icon} {finStatus.text}
                          {finAss.status === "assinado" && finAss.km_veiculo && (
                            <span style={{ color: "var(--muted)", marginLeft: 8 }}><Icon name="fleet" /> KM final: {finAss.km_veiculo}</span>
                          )}
                        </div>
                        {renderAssinaturaAuditoria(finAss, finContext)}
                      </div>
                    </>
                  );
                })()}

                {/* Seção Funcionários */}
                {(() => {
                  const funcAssinaturas = sortFuncionariosByStopOrder(
                    detalhes.assinaturas?.filter(a => a.tipo !== 'motorista' && a.tipo !== 'finalizacao') || []
                  );
                  const estimativaLabel = getEstimatedCardLabel(detalhes.trecho, detalhes.tipo_documento);

                  return (
                    <>
                      <div style={{ fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, marginBottom: 12 }}>
                        Funcionários ({funcAssinaturas.length})
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {funcAssinaturas.map((ass, i) => {
                          const assStatus = getAssinaturaStatusDisplay(ass);
                          return (
                          <div key={ass.id ?? `pp-${ass.programacao_passageiro_id}`} style={{ background: "var(--s2)", borderRadius: 10, padding: 14, border: `1px solid ${getBorderColor(ass)}` }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: '1rem' }}>{getStopOrderLabel(ass, i + 1)}. {ass.funcionario_nome}</div>
                                <div style={{ fontSize: '0.875rem', color: assStatus.color, marginTop: 4 }}>
                                  {assStatus.icon} {assStatus.text}
                                </div>
                                {ass.id_pedido_origem && (
                                  <div style={{ fontSize: '0.875rem', color: "var(--muted)", marginTop: 4 }}>
                                    <Icon name="document" /> Prog. original: {ass.id_pedido_origem}
                                  </div>
                                )}
                                {ass.cpf_informado && (
                                  <div style={{ fontSize: '0.875rem', color: "var(--muted)", marginTop: 4 }}>
                                    🪪 Chapa/CPF: {ass.cpf_informado}
                                  </div>
                                )}
                                {ass.status === "recusado" && ass.observacao_recusa && (
                                  <div style={{ fontSize: '0.875rem', color: "#fca5a5", marginTop: 4, lineHeight: 1.5 }}>
                                    📝 Motivo: {ass.observacao_recusa}
                                  </div>
                                )}
                                {getEstimatedDisplay(ass) !== '—' && (
                                  <div style={{ fontSize: '0.875rem', color: "var(--muted)", marginTop: 4 }}>
                                    🕒 {estimativaLabel}: {getEstimatedDisplay(ass)}
                                  </div>
                                )}
                                {ass.presenca && (
                                  <div style={{ fontSize: '0.875rem', marginTop: 6, fontWeight: 600, color: ass.presenca === 'presente' ? (ass.status === 'assinado' ? '#16a34a' : '#eab308') : '#ef4444' }}>
                                    {ass.presenca === 'presente'
                                      ? ass.status === 'assinado'
                                        ? 'Presente na van'
                                        : '🟡 Presente na van — assinatura pendente'
                                      : 'Ausente na van'}
                                  </div>
                                )}
                              </div>
                            </div>
                            {renderAssinaturaAuditoria(ass, documentAuditContext)}
                          </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}

                <div style={{ marginTop: 24, display: "grid", gap: 10 }}>
                  <div style={{ fontSize: '0.875rem', color: "var(--muted)", fontWeight: 700, letterSpacing: 0.5 }}>
                    {isPedidoDocument(detalhes)
                      ? "Histórico relacionado ao pedido"
                      : "Histórico da rota"}
                  </div>
                  {Array.isArray(detalhes.eventos) && detalhes.eventos.length > 0 ? (
                    <div style={{ background: "var(--s2)", borderRadius: 10, padding: 14, border: "1px solid var(--border)", display: "grid", gap: 12 }}>
                      {detalhes.eventos.map((event) => (
                        <div key={event.id} style={{ display: "grid", gap: 6, paddingBottom: 12, borderBottom: "1px solid var(--border)" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            {event.status && (
                              <span style={{
                                fontSize: '0.8125rem',
                                fontWeight: 700,
                                color: getEventStatusColor(event.status),
                                background: "var(--s3)",
                                borderRadius: 999,
                                padding: "2px 8px",
                              }}>
                                {event.status.replaceAll("_", " ")}
                              </span>
                            )}
                            <span style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
                              {formatDateTime(event.criado_em)}
                            </span>
                            <span style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
                              {event.origem || "sistema"}
                            </span>
                          </div>
                          <div style={{ fontWeight: 600, fontSize: '1rem', lineHeight: 1.45 }}>
                            {event.mensagem || event.tipo}
                          </div>
                          {event.tipo && (
                            <div style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
                              {ROUTE_EVENT_LABELS[event.tipo] || `Evento: ${event.tipo}`}
                            </div>
                          )}
                          {renderDocumentoEventDetails(event, detalhes.assinaturas || [])}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ background: "var(--s2)", borderRadius: 10, padding: 14, border: "1px solid var(--border)", color: "var(--muted)", fontSize: '0.9375rem' }}>
                      Nenhum evento registrado para esta rota.
                    </div>
                  )}
                </div>
              </>
              );
            })()}
        </Modal>
      )}

      <ConfirmModal
        open={Boolean(deleteDialog)}
        title="Excluir documento"
        message="Deseja excluir definitivamente este documento? Todas as assinaturas vinculadas também serão apagadas. Esta ação é exclusiva do superadmin e não poderá ser desfeita."
        confirmLabel="Excluir documento"
        confirmVariant="danger"
        loading={deletingId === deleteDialog?.id}
        onClose={() => {
          if (deletingId === deleteDialog?.id) return;
          setDeleteDialog(null);
        }}
        onConfirm={async () => {
          const current = deleteDialog;
          if (!current) return;
          await excluirDocumento(current);
          setDeleteDialog(null);
        }}
      />

      {toast && (
        <Toast onDismiss={dismissToast} tone={toastType === "error" ? "error" : "success"}>{toast}</Toast>
      )}
    </div>
  );
}
